import React, { useState, useEffect, useCallback, useRef } from "react";
import apiRequest from "../services/api";
import {
  FaTimes,
  FaPlus,
  FaTrash,
  FaBoxes,
  FaBoxOpen,
  FaExchangeAlt,
  FaFolderPlus,
  FaExclamationTriangle,
  FaLock,
  FaFileUpload,
  FaUpload,
  FaFileCsv,
} from "react-icons/fa";

const InventoryActionModal = ({
  isOpen,
  onClose,
  invoice, // Receives the source transaction data object
  initialType = "TRANSFER",
  onSaveSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Set explicit system flags checking the workflow stage
  const isReadOnly = invoice?.status?.toUpperCase() === "COMPLETED";

  const [actionData, setActionData] = useState({
    type: initialType,
    note: "",
    destinationBranchId: "",
    items: [],
  });

  const INVENTORY_TYPES = [
    { value: "TRANSFER", label: "Stock Transfer (Inter-Branch)" },
    { value: "DAMAGE", label: "Damaged Write-Off" },
    { value: "ADJUSTMENT_IN", label: "Stock Adjustment In (+)" },
    { value: "ADJUSTMENT_OUT", label: "Stock Adjustment Out (-)" },
  ];

  const loadLookups = useCallback(async () => {
    try {
      const productParams = new URLSearchParams({
        keyword: "",
        page: "0",
        size: "200",
      });
      const [branchRes, productsRes] = await Promise.all([
        apiRequest(`tenants/branches`),
        apiRequest(`products?${productParams}`),
      ]);
      setBranches(branchRes?.data || []);
      setProducts(productsRes?.data?.content || []);
    } catch (err) {
      setError(err?.message || "Failed to load branches / products.");
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const initialize = async () => {
      try {
        setIsLoading(true);
        setError("");
        setSelectedFile(null);

        // Load dependencies first so lookup definitions are ready for mapping
        await loadLookups();

        if (invoice) {
          const detail = await apiRequest(`invoices/${invoice.id}`);
          const rawItems = detail?.data?.items || [];

          // FIX: Safely parse backend layout which returns flat 'productId' structure
          // into the nested framework structure your row renderer uses.
          const normalizedItems = rawItems.map((item) => ({
            ...item,
            product: {
              id: item.productId || item.product?.id || "",
            },
            quantity: item.quantity ?? 1,
            purchasePrice: item.purchasePrice ?? 0.0,
            sellingPrice: item.sellingPrice ?? 0.0,
          }));

          setActionData({
            type: detail?.data?.type || invoice.type,
            note: detail?.data?.note || invoice.note || "",
            destinationBranchId: detail?.data?.destinationBranchId || "",
            items: normalizedItems,
          });
        } else {
          setActionData({
            type: initialType,
            note: "",
            destinationBranchId: "",
            items: [],
          });
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load invoice data.");
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [isOpen, initialType, invoice, loadLookups]);

  const handleAddRow = () => {
    if (isReadOnly) return;
    setActionData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product: { id: "" },
          quantity: 1,
          purchasePrice: 0.0,
          sellingPrice: 0.0,
        },
      ],
    }));
  };

  const handleRowFieldChange = (index, field, value) => {
    if (isReadOnly) return;
    const mutableItems = [...actionData.items];

    if (field === "productId") {
      // FIX: Ensure numeric exact matching type match for API integer IDs
      const targetId = Number(value);
      mutableItems[index].product = { id: targetId };

      const targetProd = products.find((p) => p.id === targetId);
      if (targetProd) {
        mutableItems[index].purchasePrice = targetProd.purchasePrice;
        mutableItems[index].sellingPrice = targetProd.sellingPrice;
      }
    } else {
      mutableItems[index][field] = value;
    }

    setActionData((prev) => ({ ...prev, items: mutableItems }));
  };

  const handleRemoveRow = (index) => {
    if (isReadOnly) return;
    setActionData((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index),
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImportUpload = async () => {
    if (isReadOnly) return;
    setError("");

    if (!selectedFile) {
      setError("Please select a file to import.");
      return;
    }

    if (actionData.type === "TRANSFER" && !actionData.destinationBranchId) {
      setError("Please select a destination branch for transfer.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Build query params for type, destinationId, note
      const params = new URLSearchParams();
      params.append("type", actionData.type);
      if (actionData.destinationBranchId) {
        params.append("destinationId", actionData.destinationBranchId);
      }
      if (actionData.note?.trim()) {
        params.append("note", actionData.note.trim());
      }

      const url = `invoices/inventory/action/import?${params.toString()}`;

      await apiRequest(url, {
        method: "POST",
        body: formData,
        // Important: do NOT set Content-Type manually when sending FormData.
        // The browser will set the correct multipart boundary.
        headers: {
          // Leave Content-Type undefined so axios/fetch sets multipart/form-data + boundary
        },
        isFormData: true, // if your apiRequest helper supports a flag
      });

      onSaveSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to import inventory action.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommitAdjustment = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    setError("");

    // Prefer import if a file is selected
    if (selectedFile) {
      await handleImportUpload();
      return;
    }

    if (actionData.items.length === 0) {
      setError("Please add at least one product or upload a file.");
      return;
    }
    if (actionData.type === "TRANSFER" && !actionData.destinationBranchId) {
      setError("Please select a destination branch.");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        type: actionData.type,
        note: actionData.note,
        items: actionData.items.map((item) => ({
          product: { id: Number(item.product?.id) },
          quantity: Number(item.quantity),
          purchasePrice: Number(item.purchasePrice),
          sellingPrice: Number(item.sellingPrice),
        })),
      };

      const url =
        actionData.type === "TRANSFER"
          ? `invoices/inventory/action?destinationId=${actionData.destinationBranchId}`
          : "invoices/inventory/action";

      await apiRequest(url, {
        method: "POST",
        body: payload,
      });

      onSaveSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to create inventory action.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="relative w-full max-w-5xl bg-slate-900/75 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.7)] text-slate-100 max-h-[90vh] flex flex-col transition-all transform scale-100">
        {/* Header Block Section */}
        <div className="flex justify-between items-center pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 border rounded-xl ${
                isReadOnly
                  ? "bg-slate-500/10 border-slate-500/20 text-slate-400"
                  : "bg-amber-500/10 border-amber-500/20 text-amber-400"
              }`}
            >
              {isReadOnly ? (
                <FaLock className="text-xl" />
              ) : (
                <FaBoxes className="text-xl" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
                Stock Operation{" "}
                {isReadOnly && (
                  <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20 uppercase font-mono tracking-wider">
                    Completed / Read-Only
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {isReadOnly
                  ? "Archived stock operation"
                  : "Branch based stock operations"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition text-slate-400 hover:text-white"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* Scrollable Form Body Container Context */}
        <form
          onSubmit={handleCommitAdjustment}
          className="overflow-y-auto py-4 space-y-6 pr-1 flex-1 text-xs"
        >
          {error && (
            <div className="p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 font-medium flex items-center gap-2">
              <FaExclamationTriangle className="flex-shrink-0" /> {error}
            </div>
          )}

          {/* Core Configuration Parameters Workspace Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-950/30 p-4 rounded-xl border border-white/5">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                <FaExchangeAlt className="text-slate-500" /> Action Type *
              </label>
              <select
                required
                disabled={isReadOnly}
                value={actionData.type}
                onChange={(e) =>
                  setActionData({ ...actionData, type: e.target.value })
                }
                className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {INVENTORY_TYPES.map((t) => (
                  <option
                    key={t.value}
                    value={t.value}
                    className="bg-slate-900"
                  >
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                Descriptive / Reason *
              </label>
              <input
                type="text"
                required
                disabled={isReadOnly}
                placeholder="Reason (e.g., Transfer to Branch B)"
                value={actionData.note}
                onChange={(e) =>
                  setActionData({ ...actionData, note: e.target.value })
                }
                className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {actionData.type === "TRANSFER" && (
            <div className="grid grid-cols-1 gap-4 bg-slate-950/30 p-4 rounded-xl border border-white/5">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                  Destination Branch *
                </label>
                <select
                  required
                  disabled={isReadOnly}
                  value={actionData.destinationBranchId}
                  onChange={(e) =>
                    setActionData((prev) => ({
                      ...prev,
                      destinationBranchId: e.target.value,
                    }))
                  }
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Destination Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Bulk Import Section */}
          {!isReadOnly && (
            <div className="bg-slate-950/30 p-4 rounded-xl border border-white/5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-white text-sm flex items-center gap-1.5">
                  <FaFileUpload className="text-amber-500" /> Bulk Import
                </h3>
                <span className="text-[10px] text-slate-500">
                  Only Excel supported
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <label className="flex-1 w-full cursor-pointer">
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed transition ${
                      selectedFile
                        ? "border-amber-500/40 bg-amber-500/5"
                        : "border-white/10 bg-slate-950/50 hover:border-amber-500/30"
                    }`}
                  >
                    <FaFileCsv className="text-amber-400 text-lg flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      {selectedFile ? (
                        <>
                          <p className="text-white text-xs font-medium truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </>
                      ) : (
                        <p className="text-slate-400 text-xs">
                          Click to select file or drag & drop
                        </p>
                      )}
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {selectedFile && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleClearFile}
                      className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-xs transition"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <p className="text-[10px] text-slate-500">
                When a file is selected, the manual item lines below are ignored
                and the import endpoint will be used.
              </p>
            </div>
          )}

          {/* Item Lines Matrix Workspace */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-white text-sm flex items-center gap-1.5">
                <FaBoxOpen className="text-amber-500" /> Item Lines
                {selectedFile && (
                  <span className="ml-2 text-[10px] text-amber-400/80 font-normal">
                    (ignored when importing file)
                  </span>
                )}
              </h3>
              {!isReadOnly && !selectedFile && (
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg transition text-[11px]"
                >
                  <FaPlus /> Add Item
                </button>
              )}
            </div>

            <div className="border border-white/5 rounded-xl overflow-hidden bg-slate-950/10">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[850px]">
                  <thead>
                    <tr className="border-b border-white/5 bg-slate-950/40 text-slate-400 text-[10px] font-bold tracking-wider uppercase">
                      <th className="px-3 py-3 w-1/3">Item *</th>
                      <th className="px-2 py-3 w-24 text-center">Quantity *</th>
                      <th className="px-2 py-3 w-28 text-right">
                        Purchase Cost
                      </th>
                      <th className="px-2 py-3 w-28 text-right">
                        Selling Price
                      </th>
                      {!isReadOnly && (
                        <th className="px-3 py-3 text-right w-12">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 font-mono text-slate-300">
                    {actionData.items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isReadOnly ? "4" : "5"}
                          className="text-center py-8 text-slate-500 font-sans italic"
                        >
                          {selectedFile
                            ? "File selected — item lines will be taken from the uploaded file."
                            : "No item lines loaded for this operation record view."}
                        </td>
                      </tr>
                    ) : (
                      actionData.items.map((item, index) => (
                        <tr key={index} className="hover:bg-white/[0.02]">
                          <td className="p-2 font-sans">
                            <select
                              required
                              disabled={isReadOnly || !!selectedFile}
                              value={String(item.product?.id ?? "")}
                              onChange={(e) =>
                                handleRowFieldChange(
                                  index,
                                  "productId",
                                  e.target.value
                                )
                              }
                              className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-white text-xs focus:outline-none focus:border-amber-500/30 disabled:opacity-75 disabled:text-slate-300 disabled:cursor-not-allowed"
                            >
                              <option
                                value=""
                                disabled
                                className="text-slate-600"
                              >
                                Choose item
                              </option>
                              {products.map((p) => (
                                <option key={p.id} value={String(p.id)}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="any"
                              min="0.001"
                              required
                              disabled={isReadOnly || !!selectedFile}
                              value={item.quantity}
                              onChange={(e) =>
                                handleRowFieldChange(
                                  index,
                                  "quantity",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-white text-xs text-center focus:outline-none disabled:opacity-75 disabled:text-slate-300 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              disabled={isReadOnly || !!selectedFile}
                              value={item.purchasePrice}
                              onChange={(e) =>
                                handleRowFieldChange(
                                  index,
                                  "purchasePrice",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-slate-400 text-xs text-right focus:outline-none disabled:opacity-75 disabled:text-slate-400 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              disabled={isReadOnly || !!selectedFile}
                              value={item.sellingPrice}
                              onChange={(e) =>
                                handleRowFieldChange(
                                  index,
                                  "sellingPrice",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-slate-500 text-xs text-right focus:outline-none disabled:opacity-75 disabled:text-slate-500 disabled:cursor-not-allowed"
                            />
                          </td>
                          {!isReadOnly && !selectedFile && (
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(index)}
                                className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/10 rounded-lg transition"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Form Actions Structural Control Row */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-slate-300 text-xs font-semibold transition"
            >
              {isReadOnly ? "Close View" : "Cancel"}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold shadow-lg shadow-amber-600/10 transition flex items-center gap-2 disabled:opacity-50"
              >
                {selectedFile ? (
                  <>
                    <FaUpload className="text-sm" />
                    {isLoading ? "Importing ..." : "Import File"}
                  </>
                ) : (
                  <>
                    <FaFolderPlus className="text-sm" />
                    {isLoading ? "Submitting ..." : "Submit"}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryActionModal;
